import moment from "moment";
import numeral from "numeral";
import {
  isString,
  isFinite,
  partition,
  reduce,
  includes,
  pickBy,
} from "lodash";
import { isDecimal, isFloat } from "validator";
import invariant from "invariant";

import DefaultHeader from "../components/documents/InvoiceHeaders/DefaultHeader";
import LayoutOneHeader from "../components/documents/InvoiceHeaders/LayoutOneHeader";
import LayoutTwoHeader from "../components/documents/InvoiceHeaders/LayoutTwoHeader";
import LayoutThreeHeader from "../components/documents/InvoiceHeaders/LayoutThreeHeader";

export const formatDateName = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();
  const suffix = getDaySuffix(day);

  return `${day}${suffix} ${month}, ${year}` || dateString
};

const getDaySuffix = (day) => {
  if (day >= 11 && day <= 13) {
    return "th";
  }
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

export const currencyMap = {
  USD: { currency: 'dollars', penny: 'cents' },
  EUR: { currency: 'euros', penny: 'cents' },
  SAR: { currency: 'riyals', penny: 'halalas' },
  AED: { currency: 'dirhams', penny: 'fils' },
  QAR: { currency: 'riyals', penny: 'dirhams' },
  KWD: { currency: 'dinars', penny: 'fils' },
  OMR: { currency: 'rials', penny: 'baizas' },
  BHD: { currency: 'dinars', penny: 'fils' },
  JOD: { currency: 'dinars', penny: 'piastres' },
  IQD: { currency: 'dinars', penny: 'fils' },
  LBP: { currency: 'pounds', penny: 'piastres' },
  EGP: { currency: 'pounds', penny: 'piastres' },
  JPY: { currency: 'yen', penny: 'sen' },
  GBP: { currency: 'pounds', penny: 'pence' },
  AUD: { currency: 'dollars', penny: 'cents' },
  CAD: { currency: 'dollars', penny: 'cents' },
  CHF: { currency: 'francs', penny: 'rappen' },
  CNY: { currency: 'yuan', penny: 'fen' },
  SEK: { currency: 'kronor', penny: 'öre' },
  NZD: { currency: 'dollars', penny: 'cents' },
  MXN: { currency: 'pesos', penny: 'centavos' },
  SGD: { currency: 'dollars', penny: 'cents' },
  HKD: { currency: 'dollars', penny: 'cents' },
  NOK: { currency: 'kroner', penny: 'øre' },
  KRW: { currency: 'won', penny: 'cent' },
  TRY: { currency: 'lira', penny: 'kuruş' },
  RUB: { currency: 'rubles', penny: 'kopeks' },
  INR: { currency: 'rupees', penny: 'paise' },
  BRL: { currency: 'reais', penny: 'centavos' },
  ZAR: { currency: 'rand', penny: 'cent' },
  TWD: { currency: 'dollars', penny: 'cents' },
  DKK: { currency: 'kroner', penny: 'øre' },
  PLN: { currency: 'zloty', penny: 'grosz' },
  THB: { currency: 'baht', penny: 'satang' },
  IDR: { currency: 'rupiahs', penny: 'cent' },
  HUF: { currency: 'forint', penny: 'fillér' },
  CZK: { currency: 'koruna', penny: 'haléř' },
  ISK: { currency: 'króna', penny: 'cent' },
  CLP: { currency: 'pesos', penny: 'centavos' },
  PHP: { currency: 'pesos', penny: 'centavos' }
};

export const LoadingSpinner = <span className="fa fa-spinner fa-spin" />

export const navTab = {
  style: {
    style: { cursor: 'pointer', fontSize: '1.05em', padding: 10 },
  },
  numbers: {
    style: { fontSize: '0.8em' },
  }
};

export const TrashIcon = <span className="fa fa-trash" />

export const getOutlineStyle = (color, size, border) => ({
  border: `${border || "2px"} solid ${color}`,
  color: color,
  padding: '0.25rem 0.5rem',
  borderRadius: '0.2rem',
  fontSize: size
});

export const SpanColor = ({ children }) => {
  const colorInheritStyle = {
    color: '#525f7f',
    fontSize: "1.2em",
  };

  return <span style={colorInheritStyle}>{children}</span>;
};

export const cStyle = {
  status: {
    style: { fontWeight: 'bold', fontSize: '1.3em', color: '#72B566' },
  },
  common: {
    className: "pt-3 pb-3",
    xs: false,
    md: 3,
  },
  full: {
    className: "pt-3 pb-3",
    xs: false,
    md: 12,
  },
};


export const titleCaseToTitle = (fieldName) => {
  const words = fieldName.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ');
  const formattedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1));
  return formattedWords.join(' ');
};

export const formatDate = (v) => (v ? moment(v).format("YYYY-MM-DD") : null);

export const isNumericString = (input) => (typeof input === 'string' && !isNaN(input))

export const renderCountSpan = (count) => (
  count === 0 || count === undefined ? null : (
    <span className="small text-white">{count > 9999 ? '(9999+)' : `(${count})`}</span>
  )
);

export function findMajorityCurrency(data) {
  if (!data || data.length === 0) return console.log("No data available") || null;

  const currencyCounts = data.reduce((counts, { CurrencyCode }) => {
    counts[CurrencyCode] = (counts[CurrencyCode] || 0) + 1;
    return counts;
  }, {});

  return Object.keys(currencyCounts).reduce((majority, currency) => (
    currencyCounts[currency] > currencyCounts[majority] ? currency : majority
  ));
}

export const getSelectedAmount = (length, Type, total, majorityCurrency) => <span>{`${length} Selected ${Type}${length > 1 ? "s" : ""} || `}<CurrencySymbol currency={majorityCurrency}/>{total}</span>;

export const formatDateTime = (v) =>
  v ? moment(v).format("YYYY-MM-DD HH:mm") : null;
export const formatCurrency = (v, format = "0,0.00") => {
  if (isString(v)) {
    v = parseFloat(v);
    if (!isFinite(v)) return "";
  }
  return numeral(v).format(format);
};
export const formatXeroDate = (v) =>
  `DateTime(${moment(v).format("YYYY, M, D")})`;
export const formatNumber = (v, format) => numeral(v).format(format || "0.00");
export const formatPercentage = (v, format) => numeral(v).format("0.[00]%");

export const isCurrency = (
  v,
  { decimal_digits = "0,2", min = 0, max } = {}
) => {
  v = (v ?? "").toString();
  return (
    isDecimal(v, pickBy({ decimal_digits })) && isFloat(v, pickBy({ min, max }))
  );
};

export const formatAccountCurrency = (v) => {
  const num = numeral(v ?? null);
  if (num.value() === null) return "";
  return num.format("(0,0.00");
};

export const calculateTaxRate = (components) => {
  const [compounded, standard] = partition(components, "IsCompound");
  let standardTotal = 1;
  let effective = 0;
  let display = 0;

  for (const i of standard) {
    standardTotal += i.Rate / 100;
    display += i.Rate * 1;
  }

  for (const i of compounded) {
    effective += standardTotal * (1 + i.Rate / 100);
    display += i.Rate * 1;
  }

  return {
    DisplayTaxRate: display.toFixed(2),
    EffectiveRate: ((effective - 1) * 100).toFixed(2),
  };
};

export const calculateTax = (value, taxRate, lineAmountType) => {
  invariant(
    includes(["NoTax", "Inclusive", "Exclusive"], lineAmountType),
    "lineAmountType must be NoTax, Inclusive or Exclusive"
  );

  const rates = {};
  const [compounded, components] = partition(
    taxRate.TaxComponents,
    "IsCompound"
  );
  let total = value;
  let tax = 0;

  for (const i of components) {
    const rate = i.Rate / 100;
    let amount = 0;

    if (lineAmountType === "Exclusive") {
      amount = value * rate;
      total += amount;
    } else if (lineAmountType === "Inclusive") {
      amount = value - value / (rate + 1);
    } else {
      continue;
    }

    rates[rate] = (rates[rate] ?? 0) + amount;
    tax += amount;
  }

  for (const i of compounded) {
    const rate = i.Rate / 100;
    let amount = 0;

    if (lineAmountType === "Exclusive") {
      amount = value * rate;
      total += amount;
    } else if (lineAmountType === "Inclusive") {
      amount = value - value / (rate + 1);
    } else {
      continue;
    }

    rates[rate] += (rates[rate] ?? 0) + amount;
    tax += amount;
  }

  return {
    components: reduce(
      rates,
      (acc, tax, rate) => {
        if (!tax) return acc;
        acc.push({
          rate,
          tax,
        });
        return acc;
      },
      []
    ),
    tax,
    total,
  };
};

export async function exportDownload(api, url = null, file_name = "exported", params = {}, payload={}, method="GET") {
  return await api({method:method, url:url, params:params, data:payload})
    .then((response) => {
      const { base64_content } = response.data;
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${base64_content}`;
      link.download = file_name;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
    })
}

export function fileDownload(api, url) {
  const path_array = url.split('/')
  const file_name = path_array[path_array.length - 1]
  return api.get("files/preview_file", { params: { 'file_path': url } })
    .then((response) => {
      const { base64_content } = response.data;
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${base64_content}`;
      link.download = file_name;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
    })
}

export const getBase64 = file => {
  return new Promise(resolve => {
    let baseURL = "";
    let reader = new FileReader();

    reader.readAsDataURL(file);
    
    reader.onload = () => {
      baseURL = reader.result;
      resolve(baseURL);
    };
  });
};

export const handleFileChange = (e, name, setValue, append) => {
  const files = Array.from(e.target.files);
  const updatedFiles = [];

  files.forEach(file => {
    getBase64(file)
      .then(result => {
        const fileData = {
          upload_file: result,
          name: file.name,
        };
        append(fileData);
        updatedFiles.push(fileData);
        
        
        if (updatedFiles.length === files.length) {
          setValue(name, updatedFiles);
        }
      })
      .catch(err => {
        console.error(err);
      });
  });
};

export const getOverdueTime = (endDate) => {
  if (!endDate) return '';

  const endDateObj = new Date(endDate);
  const now = new Date();

  if (endDateObj >= now) return '';

  const diffInMs = now - endDateObj;
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInMs / (1000 * 60 * 60 * 24 * 7));
  const diffInMonths = Math.floor(diffInMs / (1000 * 60 * 60 * 24 * 7 * 4));

  if (diffInHours < 24) {
      return `Late`;
  } else if (diffInDays < 7) {
      return `${diffInDays}d late`;
  } else  if (diffInWeeks < 4){
      return `${diffInWeeks}w late`;
  }
  else{
    return `${diffInMonths}mo late`;
}
  
};

export const convertUTCToLocal = (utcDateString) => {
  // Create a new Date object from the UTC date string
  let utcDate = new Date(utcDateString);

  // Get the local time offset (in minutes) and adjust it to milliseconds
  let offset = utcDate.getTimezoneOffset() * 60000;

  // Create a new Date object for the local time by adjusting the UTC time
  let localDate = new Date(utcDate.getTime() - offset);

  // Format the local date as desired (optional)
  let options = {
    // year: 'numeric',
    // month: '2-digit',
    // day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12:false,
  };

  return localDate.toLocaleString('en-US', options);
}

export const customDateFormat = (v, format = "YYYY-MM-DD") => (v ? moment(v).format(format || "YYYY-MM-DD") : null);

export const LAYOUT_COMPONENTS_MAP = {
  default: DefaultHeader,
  layout_one: LayoutOneHeader,
  layout_two: LayoutTwoHeader,
  layout_three: LayoutThreeHeader
}

export const getStyle = (styleSettings) => {
  let style = {}
  if (styleSettings) {
    if (styleSettings.font_family) {
      style.fontFamily = styleSettings.font_family
    }
    if (styleSettings.color) {
      style.color = styleSettings.color
    }
    if (styleSettings.font_size) {
      style.fontSize = styleSettings.font_size + 'px'
    }
    if (styleSettings.font_weight) {
      style.fontWeight = styleSettings.font_weight
    }
    if (styleSettings.alignment) {
      style.alignment = styleSettings.alignment
    }
    if (styleSettings.width) {
      style.width = styleSettings.width + 'px'
    }
    if (styleSettings.height) {
      style.height = styleSettings.height + 'px'
    }

    if (styleSettings.is_visible === false) {
      style.display = 'none'
    }

  }

  return style
}

export const getTextColor = (bgColor) => {
  const colorMap = {
      "#000000": "white",
      "#FFFFFF": "black",
      "#F5F5DC": "black",
      '#00FFFF': "black",
      '#FFFF00': "black",
      '#FDFD96': 'Pastel Yellow',
      '#B2F9FC': 'Pastel Mint',
      '#B2EBF2': 'Pastel Cyan',
  };
  return colorMap[bgColor] || "white";
};

export const convertToXeroInvoice = (data) => {
  return {
    Type: "ACCREC",
    Contact: {
      ContactID: data.connected_organisation
    },
    Date: data.issue_date,
    DueDate: data.due_date,
    InvoiceNumber: data.invoice_number,
    Reference: data.invoice_uid,
    CurrencyCode: data.currency,
    LineAmountTypes: "Inclusive",
    LineItems: data.invoice_line_items.map(item => ({
      Description: item.item_description,
      Quantity: item.quantity,
      UnitAmount: parseFloat(item.unit_price),
      ItemCode: item.item_name,
      AccountCode: "200",
      TaxType: "OUTPUT",
      TaxAmount: parseFloat(item.vat_amount),
      LineAmount: parseFloat(item.line_amount)
    })),
    SubTotal: parseFloat(data.sub_total),
    TotalTax: parseFloat(data.vat_amount),
    Total: parseFloat(data.total_amount),
    Status: data.status === "Paid" ? "AUTHORISED" : "DRAFT",
    SentToContact: data.invoice_sent
  };
}

export const languageNameMapping = {
  'en': 'English',
  'fr': 'French',
  'id-ID': 'Indonesian',
  "ar": "Arabic",
  "es": "Spanish",
  "ko": "Korean",
  "it": "Italian",
  "ja": "Japanese",
  "de-BE": "German",
  "zh": "Chinese",
  "tr": "Turkish",
  "ur": "Urdu",
  "ru": "Russian",
};

function formatErrorsToMessage(errors) {
  return Object.entries(errors)
    .map(([field, message]) => `${field}: ${message}`)
    .join('\n');
}

export function humanError(error, _default, t) {
  const errorMessages = [];

  const detail = error.response?.data?.detail || error.response?.data;

  // If detail is a string, return it directly
  if (typeof detail === 'string') {
    return t(detail || _default);
  }
  
  // If detail is an array
  if (Array.isArray(detail)) {
    detail.forEach(error => {
      // If error is a string
      if (typeof error === 'string') {
        errorMessages.push(t(error));
      }
      // If error is an object with field-message pairs
      else if (typeof error === 'object') {
        for (const [field, messages] of Object.entries(error)) {
          if (Array.isArray(messages)) {
            messages.forEach(message => {
              errorMessages.push(`${t(field)}: ${t(message)}`);
            });
          } else {
            errorMessages.push(`${t(field)}: ${t(messages)}`);
          }
        }
      }
    });
  }
  else if (typeof detail === 'object') {
    try{
      let msg = formatErrorsToMessage(detail);
      errorMessages.push(msg);
    }catch(e){}
  }

  return errorMessages.length > 0 ? errorMessages.join('\n') : _default;
}

export const trueToYes = (value) => {
  if (value === true) {
    return "Yes";
  } else if (value === false) {
    return "No";
  } else {
    return value;
  }
}

// for counting the uploaded files count in audit report
export const handleFilesCountUpdate = (count, setUploadedFilesCount, index) => {
    
  setUploadedFilesCount((prevCounts) => {
    const newCounts = [...prevCounts];
    newCounts[index] = count;
    
    return newCounts;
  });
};

export const getColorsForStages = () => {
  return [
      'pastel-green',
      'pastel-red',
      'pastel-orange',
      'pastel-aqua',
      'pastel-mint',
      'pastel-coral',
      'pastel-purple',
      'pastel-cyan',
      'light-coral',
      'bold-crimson',
      'bold-gold',
      'bold-teal',
      'bold-navy',
      'bold-emerald',
      'bold-charcoal',
      'bold-firebrick',
      'pastel-blue',
      'pastel-pink',
      'pastel-yellow',
      'pastel-lilac',
      'pastel-beige',
      'pastel-gray',
      'pastel-lime',
      'pastel-olive',
      'light-rose',
      'thistle',
      'soft-lime'
  ];
};

export const CurrencySymbol = ({currency, value=''}) => {
  if (currency === "USD") {
    return <span>$ {value}</span>;
  } else if (currency === "EUR") {
    return <span>€ {value}</span>;
  } else if (currency === "SAR") {
    return <span><i className="sar-font">§</i>{value}</span>;
  } else {
    return <span>{value} {currency || null}</span>;
  }
};

export const ID_TYPES = [
  { id: "CRN", label: "Commercial Register (CR)" },
  { id: "MOM", label: "MOMRA License" },
  { id: "MLS", label: "MLSD License" },
  { id: "SAG", label: "SAGIA License" },
  { id: "NAT", label: "National ID" },
  { id: "IQA", label: "Iqama Number" },
  { id: "PAS", label: "Passport ID" },
  { id: "GCC", label: "GCC ID" },
  { id: "700", label: "700 Number" },
  { id: "OTH", label: "Other ID" }
]

export const format_reference = (value) => value?.replace(/\(zatca-[^)]+\)/g, "").trim();

